import { z } from "zod";

const experienceLevelEnum = z.enum(["beginner", "intermediate", "advanced"], {
	message: "Required",
});

export const primaryGoalEnum = z.enum(
	["hypertrophy", "strength", "endurance", "power(sports_performance)"],
	{
		message: "Required",
	},
);

const equipmentEnum = z.enum([
	"full_gym_access",
	"barbell",
	"dumbbells",
	"kettlebells",
	"machines",
	"bands",
	"cables",
]);

export const basicAssessmentEnum = z
	.enum(["ankle", "knee", "hip", "core", "shoulder_girdle", "neck"], {
		message: "Required",
	})
	.describe("Select the area you would like to focus on.");

export const upperWarmupFocusEnum = basicAssessmentEnum.exclude(
	["ankle", "knee", "hip"],
	{
		message: "Required",
	},
);

export const lowerWarmupFocusEnum = basicAssessmentEnum.exclude(
	["core", "shoulder_girdle", "neck"],
	{
		message: "Required",
	},
);

const strengthTrainingProgramInputSchemaBase = z.object({
	experienceLevel: experienceLevelEnum,

	trainingFrequency: z
		.number({ message: "Required" })
		.min(1, "Minimum 1")
		.max(6, "Maximum 7")
		.describe("Days per week")
		.meta({
			isRange: true,
		}),

	warmupFocus: basicAssessmentEnum.optional().meta({
		title: "Warmup Focus",
		disabledMessage: "Use upper/lower focus for split programs.",
	}),

	upperWarmupFocus: upperWarmupFocusEnum.optional().meta({
		title: "Upper Body Warmup Focus",
		disabledMessage: "Only available for 4–6 day programs.",
	}),

	lowerWarmupFocus: lowerWarmupFocusEnum.optional().meta({
		title: "Lower Body Warmup Focus",
		disabledMessage: "Only available for 4–6 day programs.",
	}),

	isDailyUndulation: z.boolean().meta({
		title: "Daily Undulation",
		disabledWhen: { field: "trainingFrequency", value: 1 },
		disabledMessage: "Not available for 1 day per week.",
	}),

	primaryGoal: primaryGoalEnum,

	numberOfTrainingPhases: z
		.number({ message: "Required" })
		.min(1, "Minimum 1")
		.max(3, "Maximum 3")
		.meta({
			isRange: true,
			title: "Phases",
		}),

	sessionDuration: z
		.number({ message: "Required" })
		.min(30, "Minimum 30")
		.max(180, "Maximum 180")
		.describe("In Minutes")
		.meta({
			isRange: true,
		}),

	availableEquipment: z
		.array(equipmentEnum, {
			message: "Required",
		})
		.describe("Select at least one")
		.min(1, {
			message: "Select at least one",
		}),

	additionalNotes: z
		.string()
		.describe("Any additional notes")
		.meta({
			isMultiline: true,
		})
		.optional(),

	healthConsiderations: z
		.object({
			injuries: z
				.string()
				.describe("List any injuries")
				.meta({
					isMultiline: true,
				})
				.optional(),
			medicalConditions: z
				.string()
				.describe("List any medical conditions")
				.meta({
					isMultiline: true,
				})
				.optional(),
		})
		.optional(),

	exercisePreferences: z
		.object({
			favoriteExercises: z
				.string()
				.describe("List favorite exercises")
				.meta({
					isMultiline: true,
				})
				.optional(),
			dislikedExercises: z
				.string()
				.describe("List disliked exercises")
				.meta({
					isMultiline: true,
				})
				.optional(),
		})
		.optional(),
});

export const strengthTrainingProgramInputSchema =
	strengthTrainingProgramInputSchemaBase.superRefine((data, ctx) => {
		if (data.trainingFrequency <= 3) {
			if (!data.warmupFocus) {
				ctx.addIssue({
					code: "custom",
					path: ["warmupFocus"],
					message: "Required",
				});
			}
		} else {
			if (!data.upperWarmupFocus) {
				ctx.addIssue({
					code: "custom",
					path: ["upperWarmupFocus"],
					message: "Required",
				});
			}
			if (!data.lowerWarmupFocus) {
				ctx.addIssue({
					code: "custom",
					path: ["lowerWarmupFocus"],
					message: "Required",
				});
			}
		}
	});

const sideEnum = z.enum(["both", "l", "r", "l >", "r >"]);

const angle = z.number().min(-180).max(360).meta({
	isRange: true,
	isAngle: true,
});

const muscleLengthTestResultEnum = z
	.enum(["normal", "limited", "painful"])
	.optional();

const muscleLengthTestResultField = z
	.object({
		left: muscleLengthTestResultEnum.meta({
			defaultValue: "normal",
		}),
		right: muscleLengthTestResultEnum.meta({
			defaultValue: "normal",
		}),
	})
	.optional();

const muscleStrengthAssessmentResultEnum = z.enum([
	"normal",
	"weak",
	"with_compensation",
]);

const muscleStrengthAssessmentResultFieldValue = z
	.discriminatedUnion("value", [
		z.object({
			value: muscleStrengthAssessmentResultEnum
				.extract(["with_compensation"])
				.meta({
					defaultValue: "normal",
				}),
			compensation: z.string().describe("Describe the compensation"),
		}),
		z.object({
			value: muscleStrengthAssessmentResultEnum
				.exclude(["with_compensation"])
				.meta({
					defaultValue: "normal",
				}),
		}),
	])
	.optional();

const muscleStrengthAssessmentResultField = z
	.object({
		left: muscleStrengthAssessmentResultFieldValue,
		right: muscleStrengthAssessmentResultFieldValue,
	})
	.optional();

export const assessmentTypeEnum = z
	.enum(["basic", "advanced"], {
		message: "Required",
	})
	.describe("Choose the assessment type that best fits your needs.");

export const clinicalProgramInputSchema = z.object({
	isManualTherapyNeeded: z
		.boolean()
		.describe("Is manual therapy needed?")
		.optional(),

	overheadSquatAssessment: z
		.object({
			anteriorView: z
				.object({
					feetTurnOut: sideEnum.optional(),
					feetFlatten: sideEnum.optional(),

					kneesBowIn: sideEnum.optional(),
					kneesBowOut: sideEnum.optional(),

					armsFall: sideEnum.optional(),
				})
				.optional(),

			lateralView: z
				.object({
					excessiveForwardLean: z.boolean().optional(),
					anteriorPelvicTilt: z.boolean().optional(),
					posteriorPelvicTilt: z.boolean().optional(),

					armsFall: sideEnum.optional(),
				})
				.optional(),

			posteriorView: z
				.object({
					asymmetricalWeightShift: sideEnum.extract(["l", "r"]).optional(),
					shoulderGirdleElevation: sideEnum.optional(),
				})
				.optional(),
		})
		.meta({ title: "Overhead Squat Assessment (OSA)" })
		.optional(),

	overheadSquatAssessmentWithModifications: z
		.object({
			heelsElevated: z.boolean().optional(),
			handsDown: z.boolean().optional(),

			anteriorView: z
				.object({
					kneesBowIn: sideEnum.optional(),
					kneesBowOut: sideEnum.optional(),
				})
				.meta({
					dependsOn: ["heelsElevated", "handsDown"],
				})
				.optional(),

			lateralView: z
				.object({
					excessiveForwardLean: z.boolean().optional(),
					posteriorPelvicTilt: z.boolean().optional(),
					excessiveLordosis: z
						.boolean()
						.meta({ title: "Excessive Lordosis (APT)" })
						.optional(),
					flexion: z.boolean().optional(),
				})
				.meta({
					dependsOn: ["heelsElevated", "handsDown"],
				})
				.optional(),

			posteriorView: z
				.object({
					asymmetricalWeightShift: sideEnum.extract(["l", "r"]).optional(),
					shoulderGirdleElevation: sideEnum.optional(),
				})
				.meta({
					dependsOn: ["heelsElevated", "handsDown"],
				})
				.optional(),
		})
		.describe(
			"Requires at-least one modification to be selected (heels elevated or hands down).",
		)
		.optional(),

	goniometry: z
		.object({
			cervicalSpineLateralFlexion: z
				.object({
					left: angle
						.meta({
							defaultValue: 45,
						})
						.optional(),

					right: angle
						.meta({
							defaultValue: 45,
						})
						.optional(),
				})
				.optional(),

			shoulderExternalRotation: z
				.object({
					left: angle
						.meta({
							defaultValue: 90,
						})
						.optional(),
					right: angle
						.meta({
							defaultValue: 90,
						})
						.optional(),
				})
				.optional(),

			shoulderInternalRotation: z
				.object({
					left: angle
						.meta({
							defaultValue: 70,
						})
						.optional(),
					right: angle
						.meta({
							defaultValue: 70,
						})
						.optional(),
				})
				.optional(),

			shoulderFlexion: z
				.object({
					left: angle
						.meta({
							defaultValue: 180,
						})
						.optional(),
					right: angle
						.meta({
							defaultValue: 180,
						})
						.optional(),
				})
				.optional(),

			hipExternalRotation: z
				.object({
					left: angle
						.meta({
							defaultValue: 45,
						})
						.optional(),
					right: angle
						.meta({
							defaultValue: 45,
						})
						.optional(),
				})
				.describe("90/90 position")
				.optional(),

			hipInternalRotation: z
				.object({
					left: angle
						.meta({
							defaultValue: 45,
						})
						.optional(),
					right: angle
						.meta({
							defaultValue: 45,
						})
						.optional(),
				})
				.describe("90/90 position")
				.optional(),

			hipKneeFlexion: z
				.object({
					left: angle
						.meta({
							defaultValue: 20,
						})
						.optional(),
					right: angle
						.meta({
							defaultValue: 20,
						})
						.optional(),
				})
				.meta({
					title: "Hip/Knee Flexion",
				})
				.describe("90/90 position")
				.optional(),

			hipExtension: z
				.object({
					left: angle
						.meta({
							defaultValue: 10,
						})
						.optional(),
					right: angle
						.meta({
							defaultValue: 10,
						})
						.optional(),
				})
				.optional(),

			ankleDorsiflexion: z
				.object({
					left: angle
						.meta({
							defaultValue: 20,
						})
						.optional(),
					right: angle
						.meta({
							defaultValue: 20,
						})
						.optional(),
				})
				.optional(),
		})
		.optional(),

	muscleLengthTests: z
		.object({
			gastrocSoleusLengthTest: muscleLengthTestResultField,
			hamstringLengthTest: muscleLengthTestResultField,
			thomasTest: muscleLengthTestResultField,
			elyTest: muscleLengthTestResultField,
			obersTest: muscleLengthTestResultField.meta({ title: "Ober's Test" }),
			latLengthTest: muscleLengthTestResultField,
		})
		.optional(),

	manualMuscleTests: z
		.object({
			externalRotation: muscleStrengthAssessmentResultField,
			serratusAnterior: muscleStrengthAssessmentResultField,
			lowerTrapezius: muscleStrengthAssessmentResultField,
			core: muscleStrengthAssessmentResultField,
			gluteusMedius: muscleStrengthAssessmentResultField,
			gluteusMaximus: muscleStrengthAssessmentResultField,
			tibialisAnterior: muscleStrengthAssessmentResultField,
			tibialisPosterior: muscleStrengthAssessmentResultField,
		})
		.meta({ title: "Manual Muscle Tests (MMT)" })
		.optional(),
});

export const programTypeEnum = z.enum(["strength_training", "clinical"]);

export const programInputSchema = z
	.discriminatedUnion("programType", [
		z.object({
			programType: programTypeEnum
				.extract(["strength_training"])
				.describe("Choose the program type that best fits your needs."),
			...strengthTrainingProgramInputSchemaBase.shape,
		}),

		z.discriminatedUnion("assessmentType", [
			z.object({
				assessmentType: assessmentTypeEnum.extract(["basic"]),
				basicAssessment: basicAssessmentEnum,
				programType: programTypeEnum
					.extract(["clinical"])
					.describe("Choose the program type that best fits your needs."),
				isManualTherapyNeeded: z
					.boolean()
					.describe("Is manual therapy needed?")
					.optional(),
			}),
			z.object({
				assessmentType: assessmentTypeEnum.extract(["advanced"]),
				programType: programTypeEnum
					.extract(["clinical"])
					.describe("Choose the program type that best fits your needs."),
				...clinicalProgramInputSchema.shape,
			}),
		]),
	])
	.superRefine((data, ctx) => {
		if (data.programType !== "strength_training") return;
		if (data.trainingFrequency <= 3) {
			if (!data.warmupFocus) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["warmupFocus"],
					message: "Required",
				});
			}
		} else {
			if (!data.upperWarmupFocus) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["upperWarmupFocus"],
					message: "Required",
				});
			}
			if (!data.lowerWarmupFocus) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["lowerWarmupFocus"],
					message: "Required",
				});
			}
		}
	});

export type ProgramInputSchema = z.infer<typeof programInputSchema>;

export type StrengthTrainingProgramInput = z.infer<
	typeof strengthTrainingProgramInputSchema
>;

export type ClinicalProgramInput = z.infer<typeof clinicalProgramInputSchema>;

export type BasicAssessment = z.infer<typeof basicAssessmentEnum>;

export type UpperWarmupFocus = z.infer<typeof upperWarmupFocusEnum>;
export type LowerWarmupFocus = z.infer<typeof lowerWarmupFocusEnum>;
